import { useCallback, useEffect, useRef, useState } from 'react';

import type { PublishDrawerCorner } from '@src/stores-v2/PublishTrackingStore';
import type React from 'react';

/**
 * Drag-to-reposition for the publish progress drawer (PLAT-11083).
 *
 * The drawer is a fixed widget layered over the whole app and every corner is
 * occupied by something in at least one studio, so the user moves it out of their
 * way by grabbing it and dragging. The whole panel is the drag surface — that is
 * the point of the interaction — so the gesture is recognised anywhere except on
 * the controls inside it (see `INTERACTIVE_SELECTOR`).
 *
 * On release the drawer snaps to the corner of the quadrant the cursor is in.
 * Snapping to discrete corners rather than keeping free coordinates is what keeps
 * this predictable and, incidentally, resize-proof: every dock is edge-anchored,
 * so nothing has to be re-clamped when the window changes size or when a
 * persisted position is restored.
 *
 * Assembled from the codebase's existing drag idiom rather than a new dependency:
 * window-level listeners attached per gesture, live state mirrored through refs so
 * the handlers never read a render-time snapshot, the 4px threshold that separates
 * a click from a drag, and Escape-to-cancel — all as in `useCommentMarkerDrag`.
 * Only the gesture lives here; committing the corner is the caller's business.
 */

/**
 * Cursor travel (px) before a press becomes a drag. Matches the threshold
 * `useCommentMarkerDrag` and `VolumeKeyPoints` use.
 */
const DRAG_THRESHOLD_PX = 4;

/**
 * A press starting on one of these is that control's click, not a drag — the
 * expand caret, Close, and the per-variant download / live-link actions all sit
 * inside the drag surface.
 */
const INTERACTIVE_SELECTOR =
  'button, a, input, select, textarea, [role="button"]';

/** Live pixel offset from the docked position while a drag is in flight. */
export type DragOffset = { dx: number; dy: number };

const NO_OFFSET: DragOffset = { dx: 0, dy: 0 };

/**
 * The corner of the viewport quadrant a point falls in. Keyed off the cursor
 * rather than the panel's own rect so the drawer lands where the user pointed,
 * which is also what makes it assertable without stubbing layout.
 */
export function cornerFromPoint(
  clientX: number,
  clientY: number,
  viewportWidth: number,
  viewportHeight: number,
): PublishDrawerCorner {
  const vertical = clientY < viewportHeight / 2 ? 'top' : 'bottom';
  const horizontal = clientX < viewportWidth / 2 ? 'left' : 'right';
  return `${vertical}-${horizontal}`;
}

export interface UsePublishDrawerDrag {
  /** True once the press has travelled past the threshold. */
  dragging: boolean;
  /** Live offset to translate the panel by; zero when not dragging. */
  offset: DragOffset;
  /** Bind to the panel's `onMouseDown`. */
  startDrag: (event: React.MouseEvent<HTMLElement>) => void;
}

export function usePublishDrawerDrag(
  onCommit: (corner: PublishDrawerCorner) => void,
): UsePublishDrawerDrag {
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState<DragOffset>(NO_OFFSET);

  const gestureRef = useRef<{
    startX: number;
    startY: number;
    /** Flips once the cursor travels far enough to count as a drag. */
    moved: boolean;
    detach: () => void;
  } | null>(null);
  // The listeners are attached once per gesture, so the commit callback has to
  // be read through a ref or they'd call the one from the attaching render.
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  const endGesture = useCallback(() => {
    gestureRef.current?.detach();
    gestureRef.current = null;
    setDragging(false);
    setOffset(NO_OFFSET);
  }, []);

  // Strip listeners if the drawer unmounts mid-gesture — dismissal, a route
  // change, or the clip modal claiming the surface can all do that.
  useEffect(() => () => endGesture(), [endGesture]);

  const startDrag = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      // Left button only, and never a press that belongs to a control.
      if (event.button !== 0) return;
      if (
        event.target instanceof Element &&
        event.target.closest(INTERACTIVE_SELECTOR)
      ) {
        return;
      }
      // Suppress the text selection a press-and-sweep would otherwise start.
      event.preventDefault();

      const onMove = (ev: MouseEvent) => {
        const gesture = gestureRef.current;
        if (!gesture) return;
        const dx = ev.clientX - gesture.startX;
        const dy = ev.clientY - gesture.startY;
        if (!gesture.moved) {
          if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
          gesture.moved = true;
          setDragging(true);
        }
        setOffset({ dx, dy });
      };

      const onUp = (ev: MouseEvent) => {
        const gesture = gestureRef.current;
        endGesture();
        // A press that never travelled is a click on the panel — nothing to do,
        // and deliberately not a reposition.
        if (!gesture?.moved) return;
        onCommitRef.current(
          cornerFromPoint(
            ev.clientX,
            ev.clientY,
            window.innerWidth,
            window.innerHeight,
          ),
        );
      };

      const onKeyDown = (ev: KeyboardEvent) => {
        if (ev.key !== 'Escape' || !gestureRef.current) return;
        // Escape resolves this drag and nothing else — don't let it reach a
        // modal's close handler further down the propagation path.
        ev.preventDefault();
        ev.stopPropagation();
        endGesture();
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      window.addEventListener('keydown', onKeyDown, true);

      gestureRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        moved: false,
        detach: () => {
          window.removeEventListener('mousemove', onMove);
          window.removeEventListener('mouseup', onUp);
          window.removeEventListener('keydown', onKeyDown, true);
        },
      };
    },
    [endGesture],
  );

  return { dragging, offset, startDrag };
}
